import React, { useEffect, useState } from "react";
import axios from "axios";
import DataTable, { TableColumn } from "react-data-table-component";
import * as XLSX from "sheetjs-style";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { updateDashboardFields } from "../../../features/common/commonSlice";
import { RootState } from "../../../store";

const FaFileExcel = require("react-icons/fa").FaFileExcel;

interface MeetingData {
  zp_lgd_code: number;
  zp_name: string;
  total_meetings_uploaded: number;
  draft_mom: number;           // New Column
  final_submitted_mom: number; // New Column
}

const ZillaDashboard2: React.FC<{ finYear: string; meetingType: any }> = ({
  finYear,
  meetingType,
}) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState<MeetingData[]>([]);
  const [filterText, setFilterText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const zillaData = useSelector(
    (state: RootState) => state.common.dashboardData["Zilla Panchayats"]
  );

  useEffect(() => {
    const fetchMeetings = async () => {
      setTableData([]);
      if (!zillaData?.state_code) return;

      try {
        const response = await axios.get(`${baseUrl}api/zp_report/${zillaData.state_code}`, {
          params: {
            fin_year: finYear,
            meeting_type: meetingType.value !== "0" ? meetingType.value : undefined,
          },
        });
        
        if (response?.data?.msg_code === 400) {
          toast.error(response?.data?.msg || "Something went wrong");
          return;
        }

        const mappedData: MeetingData[] = response?.data?.data?.map((item: any) => ({
          zp_lgd_code: item.zp_lgd_code,
          zp_name: item.zp_name,
          draft_mom: item.draft_mom || 0,
          final_submitted_mom: item.final_submitted_mom || 0,
          total_meetings_uploaded: item.total_meetings_uploaded,
        })) || [];

        setTableData(mappedData);
      } catch (error) {
        console.error("Error fetching meeting data:", error);
      }
    };

    fetchMeetings();
  }, [finYear, meetingType, baseUrl, zillaData?.state_code]);

  const handleMomHighlight = (zpCode: number, zpName: string) => {
    dispatch(updateDashboardFields({
      menu: "Zilla Panchayats",
      fields: { zp_lgd_code: zpCode, zp_name: zpName },
    }));
  };

  const handleBack = () => {
    dispatch(updateDashboardFields({
      menu: "Zilla Panchayats",
      fields: { state_code: null, state_name: "", zp_lgd_code: null, zp_name: "" },
    }));
  };

  const columns: TableColumn<MeetingData>[] = [
    { name: "S.No.", cell: (_row, index) => (index + 1 + (currentPage - 1) * rowsPerPage).toString(), width: "80px" },
    {
      name: "ZP Name",
      selector: (row) => row.zp_name,
      cell: (row) => (
        <span className={row.total_meetings_uploaded > 0 ? "highlight-td" : ""}
          style={{ cursor: row.total_meetings_uploaded > 0 ? "pointer" : "default" }}
          onClick={() => row.total_meetings_uploaded > 0 && handleMomHighlight(row.zp_lgd_code, row.zp_name)}
        >
          {row.zp_name}
        </span>
      ),
      sortable: true,
    },
    { name: "Draft MoM", selector: (row) => row.draft_mom, sortable: true },
    { name: "Final Submitted", selector: (row) => row.final_submitted_mom, sortable: true },
    { name: "Total Meetings Uploaded", selector: (row) => row.total_meetings_uploaded, sortable: true },
  ];

  const filteredData = tableData.filter((item) =>
    item.zp_name.toLowerCase().includes(filterText.toLowerCase()) ||
    String(item.zp_lgd_code).includes(filterText)
  );

  const totalMeetings = filteredData.reduce((sum, r) => sum + r.total_meetings_uploaded, 0);
  const totalDraft = filteredData.reduce((sum, r) => sum + r.draft_mom, 0);
  const totalFinal = filteredData.reduce((sum, r) => sum + r.final_submitted_mom, 0);

  const exportExcel = () => {
    const header = ["S.No.", "ZP Name", "Total Meetings Uploaded", "Draft MoM", "Final Submitted"];
    const data = filteredData.map((row, index) => [index + 1, row.zp_name, row.total_meetings_uploaded, row.draft_mom, row.final_submitted_mom]);
    const totalRow = ["", "Total", totalMeetings, totalDraft, totalFinal];
    const finalData = [header, ...data, totalRow];

    const ws = XLSX.utils.aoa_to_sheet(finalData);
    ws["!cols"] = [{ wch: 8 }, { wch: 30 }, { wch: 25 }, { wch: 15 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ZillaReport");
    XLSX.writeFile(wb, "ZillaReport.xlsx");
  };

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between mb-3">
        <button className="back-btn" onClick={handleBack}>&larr; Back</button>
        <button className="shiny-btn" onClick={exportExcel}><FaFileExcel /> Export Excel</button>
      </div>
      <input type="text" className="form-control w-25 mb-2" placeholder="Search..." value={filterText} onChange={(e) => setFilterText(e.target.value)} />
      <DataTable columns={columns} data={filteredData} pagination highlightOnHover striped />

      {filteredData.length > 0 && (
        <div className="table-footer fw-bold bg-light d-flex p-2 border">
          <div style={{ width: "80px" }}></div>
          <div style={{ flex: 1 }}>Total</div>
          <div style={{ flex: 1 }}>{totalMeetings}</div>
          <div style={{ flex: 1 }}>{totalDraft}</div>
          <div style={{ flex: 1 }}>{totalFinal}</div>
        </div>
      )}
    </div>
  );
};

export default ZillaDashboard2;